import React, { useEffect, useState } from 'react'
import {
  CButton,
  CCloseButton,
  CFormInput,
  CLoadingButton,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CSpinner,
} from '@coreui/react-pro'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent'
import { InfoBox } from 'src/components/common/ImportWizard'

// Award bonus points to the players on one round's bonus card. `round` is the row from the
// rounds table; the panel is open while it is set.
const BonusPointsForm = ({ round, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false)
  const [loader, setLoader] = useState(false)
  const [rows, setRows] = useState([])
  const [teamStatus, setTeamStatus] = useState(0)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!round?.team_power_id) return
    setLoading(true)
    setRows([])
    setDirty(false)
    CommonService.bonusCardPlayer(round.team_power_id, round.id)
      .then((result) => {
        setRows(result.data || [])
        setTeamStatus(result.teamStatus)
      })
      .catch(() => ToastComponent('Could not load the bonus card players', 'error'))
      .finally(() => setLoading(false))
  }, [round])

  const setBonus = (index, value) => {
    setDirty(true)
    setRows((current) => current.map((row, i) => (i === index ? { ...row, bonus: value } : row)))
  }

  const save = () => {
    setLoader(true)
    CommonService.saveBonusCardPlayerPoint({ data: rows })
      .then((res) => {
        if (res.status === 200) {
          ToastComponent(res.message, 'success')
          onSaved()
          onClose()
        } else {
          ToastComponent(res.message, 'error')
        }
      })
      .catch(() => ToastComponent('Something Went wrong.Please try again', 'error'))
      .finally(() => setLoader(false))
  }

  const canSave = teamStatus === 1 && rows.length > 0

  return (
    <COffcanvas placement="end" visible={!!round} onHide={onClose} backdrop style={{ width: 520 }}>
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Round {round?.round} Bonus Points</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            {round?.bonus_card_name ? `${round.bonus_card_name} - ` : ''}bonus points per player.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={onClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        <InfoBox className="small">
          Round Points are what each player scored in Round {round?.round}. The bonus you enter is
          awarded on top.
        </InfoBox>

        {loading ? (
          <div className="d-flex align-items-center gap-2 text-medium-emphasis py-4">
            <CSpinner size="sm" />
            <span>Loading players...</span>
          </div>
        ) : rows.length === 0 ? (
          <div className="bc-empty">
            No players on this bonus card yet. Add some from <strong>Edit Bonus Card</strong>.
          </div>
        ) : (
          <table className="bc-points table align-middle mb-0">
            <thead>
              <tr>
                <th>Player</th>
                <th className="text-end">Round Points</th>
                <th className="text-end">Bonus</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id}>
                  <td className="bc-points-name" title={row.player_name}>
                    {row.player_name || 'Player removed'}
                  </td>
                  <td className="text-end bc-num">{row.fantasy_points ?? 0}</td>
                  <td className="text-end">
                    <CFormInput
                      type="number"
                      step="any"
                      size="sm"
                      className="bc-points-input"
                      aria-label={`Bonus for ${row.player_name}`}
                      name={`data.${index}.bonus`}
                      value={row.bonus ?? ''}
                      placeholder="0"
                      onChange={(e) => setBonus(index, e.target.value)}
                      onFocus={(e) => e.target.select()}
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={onClose}>
          Cancel
        </CButton>
        {canSave && (
          <CLoadingButton color="primary" loading={loader} disabled={!dirty} onClick={save}>
            Save Bonus Points
          </CLoadingButton>
        )}
      </div>
    </COffcanvas>
  )
}

export default BonusPointsForm
